import {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
} from "react";
import { Alert, Box, useMediaQuery, useTheme } from "@mui/material";
import { MapContainer, TileLayer, useMap, useMapEvents } from "react-leaflet";
import ToolBarMaps from "../../../../../componentes/easyMaps/toolbarMapsPublicRp";
import L from "leaflet";
import "../../../../../componentes/leaflet-migrations";
import "../../../../../componentes/leaflet-fullscreen/Leaflet.fullscreen";
import "leaflet-routing-machine";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import TitleBar from "../../../../../componentes/easyMaps/titleBarPublic";
import ContextMenu from "../../../../../componentes/easyMaps/contextMenu";
import AlertModal from "../../../../../componentes/alertModal";
import DescriptionBar from "../../../../../componentes/easyMaps/descriptionBar";
import { Link, useOutletContext } from "react-router-dom";

import PixiOverlay from "./pixiOverlayIcons";
import useTextures from "./hooks/useTextures";
import { PolylineCustom } from "../../geolocalizadoONUs/leaflatMapa/polyLine";
import RightBarEasyMapsOnus from "../../../../../componentes/easyMaps/rightBarEasyMapsOnus";
import { Close } from "@mui/icons-material";
import RulerComponent from "../../../../../componentes/easyMaps/rulerPoints";
import { useMapa } from "../../../../../contexts/mapaContext";
import FilterCities from "../../../../../componentes/easyMaps/filterCities";

dayjs.extend(utc);
dayjs.extend(timezone);

const LeafletMapa = forwardRef((props, ref) => {
  const {
    title,
    setFullScreem,
    fullScreem,
    invalidElements = [],
    setElementSelected = () => {},
    setElementONUSelected = () => {},
    connectionSelected,
    setConnectionSelected = () => {},
    elementSelected,
    elementONUSelected,
    iconsElements = [],
    mapId,
    setSnackbar,
    editMode,
    setEditMode,
    checkChanges,
    setCheckChanges,
    dataONUS,
    boundsCoordinates,
    centerCoordinates = [-10, -48],
    initialZoom,
    isDateRecent,
    collapse,
  } = props;

  const [lockMode, setLockMode] = useState(false);
  const [showLabel, setShowLabel] = useState(false);
  const [elementsONUs, setElementsONUs] = useState([]);
  const [openRightBar, setOpenRightBar] = useState(false);
  const [contextMenuFlow, setContextMenuFlow] = useState(null);
  const [openAlertModal, setOpenAlertModal] = useState(null);
  const [openConnection, setOpenConnection] = useState(null);
  const [showLabelEnlace, setShowLabelEnlace] = useState(true);
  const [rulerMode, setRulerMode] = useState(false);
  const [rulerPoint, setRulerPoint] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(!isDateRecent);
  const [focusNodeId, setFocusNodeId] = useState(null);
  const [citiesSwitchMakerVisible, setCitiesSwitchMakerVisible] = useState([]);
  const mapRef = useRef(null);
  const didSetInitialZoom = useRef(false);
  const shouldGetZoomRef = useRef(false);
  const {
    configurationMap,
    cities,
    citiesFilter,
    setCitiesFilter,
    allElementsMap,
    connections,
    mapType,
  } = useMapa();
  const theme = useTheme();
  const { config } = useOutletContext();

  const [markers, setMarkers] = useState([]);
  const [currentClusters, setCurrentClusters] = useState([]);
  const [selectedCtoOnus, setSelectedCtoOnus] = useState([]);

  const isSm = useMediaQuery(theme.breakpoints.down("lg"));

  // Mapas para otimizar buscas
  const markersMap = useRef(new Map()); // id -> marker
  const ctoTitleMap = useRef(new Map()); // ctoTitleUpperCase -> cto marker
  const onusByCtoMap = useRef(new Map()); // ctoId -> onus array
  const serialToIdMap = useRef(new Map()); // serial -> onu id

  /* ---------- MEMO ---------- */
  const onusInvalidas = useMemo(() => {
    return (invalidElements ?? []).filter((el) => el.tipoElemento === "onu");
  }, [invalidElements]);

  // Função para atualizar os mapas de referência
  const updateReferenceMaps = useCallback((markersArray) => {
    const newMarkersMap = new Map();
    const newCtoTitleMap = new Map();
    const newOnusByCtoMap = new Map();
    const newSerialToIdMap = new Map();

    markersArray.forEach((marker) => {
      // Mapa por id
      newMarkersMap.set(marker.id, marker);

      // Mapa por serial
      if (marker.tipoElemento === "onu") {
        const serial = (marker.id ?? "")
          .replace(/_onu$/i, "")
          .trim()
          .toUpperCase();
        newSerialToIdMap.set(serial, marker.id);
      }

      // Mapa por título de CTO
      if (marker.tipoElemento === "cto") {
        const ctoTitle = (marker.titulo ?? "").trim().toUpperCase();
        newCtoTitleMap.set(ctoTitle, marker);

        // Mapa de ONUs por CTO
        if (marker.onus?.length) {
          newOnusByCtoMap.set(marker.id, marker.onus);
        }
      }
    });

    markersMap.current = newMarkersMap;
    ctoTitleMap.current = newCtoTitleMap;
    onusByCtoMap.current = newOnusByCtoMap;
    serialToIdMap.current = newSerialToIdMap;
  }, []);

  const filteredMarkers = useMemo(() => {
    let filtered = markers.filter((m) => m?.hasCto !== false);
    const esconderOnu =
      configurationMap?.configuracoes?.esconderOnu != null
        ? configurationMap?.configuracoes?.esconderOnu
        : true;

    if (mapType != 4 ? esconderOnu : false) {
      filtered = markers.filter(
        (m) =>
          m.tipoElemento === "cto" ||
          selectedCtoOnus.some((onu) => onu.id === m.id && onu.hasCto === true)
      );
    }

    if (configurationMap?.configuracoes?.showOnusWithoutCto) {
      filtered = filtered.concat(
        markers.filter((m) => m.tipoElemento === "onu" && m.hasCto === false)
      );
    }
    if (elementONUSelected || elementSelected) {
      filtered = filtered.concat(
        markers.filter(
          (m) => m.id === elementONUSelected?.id || m.id === elementSelected?.id
        )
      );
    }

    // Atualizar cidades visíveis
    setCitiesSwitchMakerVisible(
      [...new Set(filtered.map((m) => m.codeCity))].flat()
    );

    // Filtro de cidades
    if (citiesFilter.length > 0) {
      const cityCodes = new Set(citiesFilter.map((c) => c.codigo));
      filtered = filtered.filter((m) => {
        const arr = m.codeCity;
        if (!arr) return false;

        // Usar Set para otimizar a busca
        const citySet = new Set(arr);
        for (const code of cityCodes) {
          if (citySet.has(code)) return true;
        }
        return false;
      });
    }

    return filtered;
  }, [
    markers,
    configurationMap?.configuracoes?.esconderOnu,
    selectedCtoOnus,
    elementONUSelected,
    configurationMap?.configuracoes?.showOnusWithoutCto,
    citiesFilter,
  ]);

  function colorEnlace(power) {
    if (power == null || power == 0 || power === Infinity || !config) {
      return theme.palette.enlaces.offline.main;
    }

    const escala = config?.configuracoes?.ftth?.camadaFTTH?.escala;
    if (!escala || !Array.isArray(escala)) {
      return theme.palette.enlaces.offline.main;
    }

    // Encontrar a cor correspondente
    for (let i = escala.length - 1; i >= 0; i--) {
      if (parseFloat(escala[i]?.label) >= power) {
        return escala[i]?.cor || theme.palette.enlaces.offline.main;
      }
    }

    return theme.palette.enlaces.offline.main;
  }

  useEffect(() => {
    setSnackbarOpen(!isDateRecent);
  }, [isDateRecent]);

  useEffect(() => {
    const ftthEscalaNum = (
      config?.configuracoes?.ftth?.camadaFTTH?.escala ?? []
    )
      .map((e) => parseFloat(e?.label))
      .filter(Number.isFinite)
      .sort((a, b) => a - b);

    const selected = new Set(
      (Array.isArray(configurationMap?.configuracoes?.camadaFTTH?.filtros)
        ? configurationMap?.configuracoes?.camadaFTTH?.filtros
        : []
      ).map((v) => String(v))
    );

    const bucketLabel = (p) => {
      const rx = Number.parseFloat(p);
      if (!Number.isFinite(rx) || ftthEscalaNum.length === 0)
        return "Sem Dados";

      const topo = ftthEscalaNum[ftthEscalaNum.length - 1];
      if (rx > topo) return "Sem Dados";

      const idx = ftthEscalaNum.findIndex((c) => c >= rx);
      const ceil = idx >= 0 ? ftthEscalaNum[idx] : topo;
      return String(ceil);
    };

    // Coletar todos os elementos de forma otimizada
    const allElements = Array.from(allElementsMap.values());

    const mks = allElements.map((element) => {
      const base = element.tipoElemento === "cto" ? ["cto"] : ["house"];

      // Obter coordenadas com fallback seguro
      const lat = parseFloat(element?.coordenadas?.lat);
      const lng = parseFloat(element?.coordenadas?.long);
      const position = [isNaN(lat) ? 0 : lat, isNaN(lng) ? 0 : lng];

      return {
        ...element,
        position,
        iconId: mapType !== 4 ? base : ["camera"],
        baseIcon: base,
        tint: colorEnlace(element?.data?.potencia?.rx),
        label: element.titulo ?? element.id,
      };
    });

    // Filtrar marcadores
    const filteredMks = mks.filter((m) => {
      if (selected.size === 0) return true;
      const label = bucketLabel(m?.data?.potencia?.rx);
      return selected.has(label);
    });

    setMarkers(filteredMks);
    updateReferenceMaps(filteredMks);
  }, [
    config,
    configurationMap?.configuracoes?.camadaFTTH?.filtros,
    allElementsMap,
    updateReferenceMaps,
  ]);

  const handleClick = useCallback(
    (id) => {
      if (rulerMode) return;
      const found = markersMap.current.get(id);
      if (!found) return;

      if (found.tipoElemento === "onu") {
        setElementONUSelected(found);

        // Buscar CTO correspondente usando o mapa
        const ctoTitle = (found.cto ?? "").trim().toUpperCase();
        const ctoMarker = ctoTitleMap.current.get(ctoTitle);

        if (ctoMarker) {
          setElementSelected(ctoMarker);
          const ctoOnusIds = onusByCtoMap.current.get(ctoMarker.id) || [];
          const ctoOnus = ctoOnusIds
            .map((onuId) => markersMap.current.get(onuId))
            .filter(Boolean);

          setSelectedCtoOnus([ctoMarker, ...ctoOnus]);
        } else {
          setSelectedCtoOnus([found]);
        }
      } else {
        setElementSelected(found);
        setElementONUSelected(null);
        const ctoOnusIds = onusByCtoMap.current.get(found.id) || [];
        const ctoOnus = ctoOnusIds
          .map((onuId) => markersMap.current.get(onuId))
          .filter(Boolean);
        setSelectedCtoOnus(ctoOnus);
      }
    },
    [rulerMode, setElementSelected, setElementONUSelected]
  );

  const handleBackgroundClick = useCallback(() => {
    setElementSelected(null);
    setElementONUSelected(null);
    setFocusNodeId(null);
    setSelectedCtoOnus([]);
  }, [setElementSelected, setElementONUSelected]);

  const handleClusterClick = useCallback(
    (cluster) => {
      if (!mapRef.current || rulerMode) return;
      const map = mapRef.current;
      const currentZoom = map.getZoom();
      const newZoom = Math.min(currentZoom + 2, 19);

      map.setView(L.latLng(cluster.center), newZoom);
    },
    [rulerMode]
  );

  /* ---------- TEXTURAS ---------- */
  const texturesReady = useTextures();

  //===============================================================

  useEffect(() => {
    setTimeout(() => {
      if (mapRef.current && mapRef.current.invalidateSize) {
        mapRef.current.invalidateSize();
      }
    }, 300);
  }, [collapse]);

  useEffect(() => {
    if (!mapRef.current) return;
    SetMaxBounds();
    mapRef.current.invalidateSize();
  }, [mapRef.current]);

  useEffect(() => {
    if (!rulerMode) return;

    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "z") {
        e.preventDefault();
        removeLastEl();
      }
    };

    const handleMouseClick = (e) => {
      if (e.button === 2) {
        e.preventDefault();
        removeLastEl();
      }
    };

    const handleContextMenu = (e) => {
      if (rulerMode) {
        e.preventDefault();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("mousedown", handleMouseClick);
    window.addEventListener("contextmenu", handleContextMenu);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("mousedown", handleMouseClick);
      window.removeEventListener("contextmenu", handleContextMenu);
    };
  }, [rulerMode]);

  useEffect(() => {
    if (!elementSelected?.id) setElementSelected(null);

    if (editMode === false) {
      const currentMarker = markersMap.current.get(elementSelected?.id);
      if (currentMarker) {
        setElementSelected(currentMarker);
      }
    }

    if (editMode === false && elementSelected?.id) {
      const stillThere = markersMap.current.has(elementSelected.id);
      if (!stillThere) setElementSelected(null);
    }

    setConnectionSelected(null);
  }, [
    editMode,
    showLabel,
    markers,
    elementSelected?.id,
    setElementSelected,
    setConnectionSelected,
  ]);

  const getSerial = useCallback(
    (id) => (id ?? "").replace(/_onu$/i, "").trim().toUpperCase(),
    []
  );

  const allOnus = useMemo(
    () => [...markers, ...onusInvalidas],
    [markers, onusInvalidas]
  );

  const serial = useMemo(
    () => (dataONUS?.serialOnu ?? "").trim().toUpperCase(),
    [dataONUS?.serialOnu]
  );

  const filteredOnus = useMemo(() => {
    if (!elementSelected?.id) return [];

    const element = markersMap.current.get(elementSelected.id);
    if (!element) return [];

    if (element.tipoElemento === "cto") {
      const onusIds = onusByCtoMap.current.get(element.id) || [];
      return onusIds
        .map((id) => allOnus.find((el) => el.id === id))
        .filter(Boolean);
    }

    // Se for ONU, buscar sua CTO
    const ctoTitle = (element.cto ?? "").trim().toUpperCase();
    const ctoMarker = ctoTitleMap.current.get(ctoTitle);

    return ctoMarker
      ? (onusByCtoMap.current.get(ctoMarker.id) || [])
          .map((id) => allOnus.find((el) => el.id === id))
          .filter(Boolean)
      : [element];
  }, [elementSelected, allOnus]);

  const mergedOnus = useMemo(() => {
    if (!serial || !dataONUS) return filteredOnus;

    const extra = formatExtraData(dataONUS) ?? {};

    return filteredOnus.map((onu) => {
      const matchOnu = getSerial(onu.id) === serial;
      if (!matchOnu) return onu;

      return {
        ...onu,
        data: {
          ...(onu.data ?? {}),
          ...extra,
        },
      };
    });
  }, [filteredOnus, serial, dataONUS, getSerial]);

  useEffect(() => {
    const cood = elementONUSelected?.position || elementSelected?.position;
    if (cood?.length) focusZoomCoordenates(cood[0], cood[1]);
  }, [elementSelected?.position, elementONUSelected?.position]);

  useEffect(() => {
    if (!elementSelected?.id) {
      setElementsONUs([]);
      //setElementONUSelected(null);
      return;
    }

    setElementsONUs(mergedOnus);

    const found = serial
      ? mergedOnus.find((o) => getSerial(o.id) === serial)
      : mergedOnus.find((o) => o.id === elementSelected.id) || null;

    if (elementSelected.tipoElemento === "onu" && found) {
      setElementONUSelected(found);
    }
  }, [
    elementSelected?.id,
    mergedOnus,
    serial,
    getSerial,
    setElementONUSelected,
  ]);

  const SetMaxBounds = () => {
    if (!mapRef.current) return;

    const southWest = L.latLng(-89.98155760646617, -180);
    const northEast = L.latLng(89.99346179538875, 180);
    const bounds = L.latLngBounds(southWest, northEast);

    mapRef.current.setMaxBounds(bounds);
  };

  const clearRuler = () => {
    setRulerPoint([]);
    setRulerMode(false);
  };

  const removeLast = () => {
    setRulerPoint((prev) => {
      if (prev.length === 0) return prev;
      return prev.slice(0, -2);
    });
  };

  const removeLastEl = () => {
    setRulerPoint((prev) => {
      if (prev.length === 0) return prev;
      return prev.slice(0, -1);
    });
  };

  const styles = {
    paperContainer: {
      padding: "16px ",
      display: "flex",
      alignItems: "center",
      position: "relative",
      width: "100%",
      borderRadius: 3,
      mt: 5.5,
    },
    body: {
      width: "100%",
      height: "100%",
      position: "absolute",
      display: "flex",
      flexDirection: "column",
      left: "0px",
      top: "0px",
      overflow: "hidden",
    },
    mapInfoStack: {
      padding: "12px 16px",
      width: "250px",
    },
    mapInfoDataStack: {
      padding: "12px 16px",
      width: "120px",
    },
    mapInfoTitle: {
      fontSize: "0.75rem",
      lineHeight: 1.25,
      fontWeight: 600,
      letterSpacing: "0.033em",
      color: "primary.main",
    },
    mapInfoText: {
      fontSize: "0.875rem",
      lineHeight: 1.5,
      fontWeight: 400,
      textTransform: "capitalize",
    },
    fontPopup: {
      margin: "0px",
      fontSize: "10px",
      fontWeight: 500,
      color: theme.palette.color.textEasyMaps,
    },
    titlePopup: {
      margin: "0px",
      fontSize: "12px",
      fontWeight: 500,
      color: theme.palette.color.textEasyMaps,
      width: "100%",
      textAlign: "center",
      marginBottom: "10px",
      whiteSpace: "normal",
      overflow: "hidden",
      textOverflow: "ellipsis",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
    },
  };

  function formatExtraData(obj) {
    return {
      statusContrato: { value: obj.statusContrato ?? "", unit: "" },
      pop: { value: obj.popCliente ?? "", unit: "" },
      desconexoes: { value: obj.desconexoesCliente ?? 0, unit: "x" },
      posicao: { value: obj.posicaoOnu ?? "", unit: "" },
      ip: { value: obj.ipCliente ?? "", unit: "" },
      plano: { value: obj.planoContrato ?? "", unit: "" },
      valorPlano: { value: obj.valorPlano ?? 0, unit: "Reais" },
      localizacao: {
        cep: obj.cepCliente ?? "",
        bairro: obj.bairroCliente ?? "",
        rua: obj.enderecoCliente ?? "",
        numero: obj.numeroEndereco ?? "",
      },
    };
  }

  const formatPoints = (edge) => {
    const nodeStart =
      markersMap.current.get(edge.referenceId) ||
      (edge.referenceId === elementSelected?.id ? elementSelected : null);
    const nodeEnd =
      markersMap.current.get(edge.destinationId) ||
      (edge.destinationId === elementSelected?.id ? elementSelected : null);

    const nodeStartPosition = {
      lat: parseFloat(nodeStart?.coordenadas?.lat) || 0,
      long: parseFloat(nodeStart?.coordenadas?.long) || 0,
      active: false,
    };

    const nodeEndPosition = {
      lat: parseFloat(nodeEnd?.coordenadas?.lat) || 0,
      long: parseFloat(nodeEnd?.coordenadas?.long) || 0,
      active: false,
    };

    return [
      nodeStartPosition,
      ...(edge.positionHandlers ?? []).map((a) => ({
        lat: a.y || 0,
        long: a.x || 0,
        active: false,
      })),
      nodeEndPosition,
    ];
  };

  function zoom(action) {
    if (!mapRef.current) return;
    const map = mapRef.current;
    let zoom = map.getZoom();
    // garante que o mapa não fique deslizando
    if (zoom > 19 && action > 0) return;
    if (zoom < 1 && action < 0) return;
    map.setZoom(zoom + action);
  }
  function focusZoom(id) {
    if (!id) return;
    const element = markersMap.current.get(id);
    if (!element) return;

    const cood = element.position;
    if (cood.length) {
      focusZoomCoordenates(cood[0], cood[1]);
      setFocusNodeId(id);
    }
  }

  function focusZoomCoordenates(lat, lng) {
    const forceZoom = shouldGetZoomRef.current;

    if (isNaN(lat) || isNaN(lng)) return;
    const map = mapRef.current;
    if (!elementSelected)
      forceZoom ? map?.setView([lat, lng], 18) : map?.setView([lat, lng]);
    else
      forceZoom
        ? map?.setView([lat, parseFloat(lng) + 0.0003], 18)
        : map?.setView([lat, parseFloat(lng) + 0.0003]);
    shouldGetZoomRef.current = false;
  }

  useImperativeHandle(ref, () => ({
    focusZoomExt(id) {
      if (!id) return;

      handleClick(id);
      shouldGetZoomRef.current = true;
    },
  }));

  function Events() {
    const map = useMap();
    const moveTimeout = useRef();

    useMapEvents({
      click(e) {
        if (rulerMode) {
          setRulerPoint((prev) => [...prev, e.latlng]);
        }
      },
      move(e) {
        if (moveTimeout.current) clearTimeout(moveTimeout.current);
        moveTimeout.current = setTimeout(() => {
          const center = map?.getBounds()?.getCenter();
          const zoom = map.getZoom();
          const url = new URL(window.location.href);
          url.searchParams.set("lat", center.lat);
          url.searchParams.set("lng", center.lng);
          url.searchParams.set("zoom", zoom);
          window.history.replaceState({}, "", url);
        }, 200);
      },
    });
    return null;
  }

  const SetInitialZoom = () => {
    const map = useMap();

    useEffect(() => {
      if (!map || didSetInitialZoom.current) return;

      if (initialZoom && centerCoordinates) {
        didSetInitialZoom.current = true;
        return;
      }

      if (Array.isArray(boundsCoordinates) && boundsCoordinates.length) {
        const dataBounds = L.latLngBounds(boundsCoordinates);
        map.fitBounds(dataBounds, {
          maxZoom: 14,
        });
        didSetInitialZoom.current = true;
      }
    }, [map]);

    return null;
  };

  // Otimizar busca de conexões

  const filteredConnections = useMemo(() => {
    if (!elementSelected?.id) return [];

    const filteredMarkerIds = new Set(filteredMarkers.map((m) => m.id));

    const expectedReferenceId =
      elementSelected.tipoElemento === "cto"
        ? elementSelected.id
        : `${elementSelected.cto?.replace(/\s/g, "")}_cto`;

    return Array.from(connections.values()).filter((conn) => {
      const onu = markersMap.current.get(conn.destinationId);
      if (!onu) return false;

      if (!filteredMarkerIds.has(onu.id)) return false;

      const [lat, lng] = onu.position || [];
      if (!lat || !lng) return false;

      if (onu.hasCto === false) return false;

      return conn.referenceId === expectedReferenceId;
    });
  }, [
    connections,
    filteredMarkers,
    elementSelected?.id,
    elementSelected?.tipoElemento,
    elementSelected?.cto,
  ]);

  return (
    <>
      <Box sx={{ overflow: "visible", zIndex: 1202 }}>
        <TitleBar
          title={title}
          id={mapId}
          setSnackbar={setSnackbar}
          center={elementSelected != null || connectionSelected != null}
        />
      </Box>
      <Box sx={styles.body}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
          }}
        >
          {snackbarOpen && (
            <Box
              sx={{
                position: "absolute",
                borderRadius: 2,
                width: { xs: "%100", sm: "calc(100vw - 278px)" },
                ml: { xs: "22vw", lg: "150px" },
                py: 2,
                zIndex: 2,
                display: "flex",
                justifyContent: "center",
                gap: 1.5,
              }}
            >
              <Alert
                severity="warning"
                onClose={() => setSnackbarOpen(false)}
                action={
                  <Close
                    fontSize="small"
                    onClick={() => setSnackbarOpen(false)}
                    sx={{ cursor: "pointer" }}
                  />
                }
                sx={{
                  alignItems: "center",
                  width: {
                    xs: "80vw",
                    xsm: "300px",
                    sm: "300px",
                    md: "300px",
                    mdl: "300px",
                    lg: "400px",
                    xl: "635px",
                  },
                }}
              >
                Os dados do mapa estão desatualizados. Por favor, entre em{" "}
                <Link
                  href="https://wa.me/5584994581475"
                  color={theme.palette.warning.main}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  contato com o suporte.
                </Link>
              </Alert>
            </Box>
          )}

          <ToolBarMaps
            editMode={editMode}
            setEditMode={setEditMode}
            fullScreem={fullScreem}
            setFullScreem={setFullScreem}
            showLabel={showLabel}
            setShowLabel={setShowLabel}
            lockMode={lockMode}
            setLockMode={setLockMode}
            setShowLabelEnlace={setShowLabelEnlace}
            showLabelEnlace={showLabelEnlace}
            setOpenAlertModal={setOpenAlertModal}
            checkChanges={checkChanges}
            setShowRuler={setRulerMode}
            setRulerPoint={setRulerPoint}
            showRuler={rulerMode}
            tipo={0}
            isFtth={true}
            config={config}
          >
            <ToolBarMaps.InputZoomGeo zoom={zoom} />
          </ToolBarMaps>

          <FilterCities
            data={cities}
            elementsFilter={citiesFilter}
            setElementsFilter={setCitiesFilter}
            citiesSwitchMakerVisible={citiesSwitchMakerVisible}
          />

          {!editMode && mapType != 4 && (
            <DescriptionBar
              key={"descrptionbar"}
              variant="ftth"
              elementSelected={!!elementSelected}
            />
          )}

          <MapContainer
            fullscreenControl={false}
            ref={mapRef}
            key={`mapaSingle`}
            id="map"
            center={centerCoordinates}
            zoom={initialZoom ?? 4}
            minZoom={3}
            zoomControl={false}
            style={{
              width: "100%",
              height: "100%",
              position: "absolute",
              zIndex: 0,
            }}
          >
            <TileLayer
              url={
                theme.palette.mode == "dark"
                  ? config?.configuracoes?.ftth?.mapaTile?.dark ||
                    "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  : config?.configuracoes?.ftth?.mapaTile?.light ||
                    "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              }
              maxZoom={19}
              detectRetina={true}
            />
            <Events />
            {rulerMode && (
              <RulerComponent
                rulerPoints={rulerPoint}
                removeLast={removeLast}
                onClearRuler={clearRuler}
              />
            )}
            <SetInitialZoom />

            {texturesReady && (
              <PixiOverlay
                key={"pixiFtth"}
                markers={filteredMarkers}
                enableClustering={mapRef?.current?.getZoom() <= 17}
                onClustersChange={setCurrentClusters}
                elementSelected={elementSelected}
                elementONUSelected={elementONUSelected}
                selectedCtoOnus={selectedCtoOnus}
                focusNodeId={focusNodeId}
                onClick={handleClick}
                onBackgroundClick={handleBackgroundClick}
                onClusterClick={handleClusterClick}
                configLocal={configurationMap?.configuracoes?.ftth}
                showLabel={showLabel}
                rulerMode={rulerMode}
                configurationMap={configurationMap}
              />
            )}

            {elementSelected &&
              filteredConnections.map((conn, index) => {
                const position = formatPoints(conn);
                if (!editMode) {
                  const destination = markersMap.current.get(
                    conn.destinationId
                  );
                  return (
                    <PolylineCustom
                      darkMode={theme.palette.mode == "dark"}
                      variant="ftth"
                      percent={destination?.data?.potencia?.rx}
                      host={destination?.titulo}
                      data={{
                        ...conn?.data,
                        powerRx: destination?.data?.potencia?.rx,
                        powerTx: destination?.data?.potencia?.tx,
                      }}
                      isDown={!!!destination?.data?.status?.value}
                      key={`${conn.id}-selected-${index}`}
                      positions={position.map((a) => [a.lat || 0, a.long || 0])}
                      animation={"pontilhada"}
                      onClick={(e) => {
                        L.DomEvent.stopPropagation(e);
                        focusZoom(conn.destinationId);
                        setOpenConnection(conn.destinationId);
                      }}
                    />
                  );
                }
                return null;
              })}
          </MapContainer>

          <RightBarEasyMapsOnus
            mapType={mapType}
            mapId={mapId}
            setElementONUSelected={setElementONUSelected}
            elementONUSelected={elementONUSelected}
            setFocusNodeId={setFocusNodeId}
            focusZoom={focusZoom}
            open={openRightBar}
            setOpen={setOpenRightBar}
            //elements={filteredMarkers}
            //connections={connections}
            setElement={() => {
              setElementONUSelected(null);
              setElementSelected(null);
              setSelectedCtoOnus([]);
            }}
            setConnections={() => {}}
            iconsElement={iconsElements}
            element={
              !editMode && elementONUSelected?.id
                ? elementONUSelected
                : elementSelected?.id
                ? elementSelected
                : null
            }
            fullScreem={fullScreem}
            openConnection={openConnection}
            setOpenConnection={setOpenConnection}
            link={`/easyMaps/mapa/${
              elementSelected?.mapRef?.tipo === 0 ? "geo" : "top"
            }/${elementSelected?.mapRefId ?? ""}`}
            elementsONUs={elementsONUs}
          />
        </Box>

        <ContextMenu
          contextMenu={contextMenuFlow}
          setContextMenu={setContextMenuFlow}
          editMode={editMode}
          setEditMode={setEditMode}
        />

        <AlertModal
          openModal={openAlertModal?.open ?? false}
          setOpenModal={(data) => setOpenAlertModal({ open: data })}
          buttonText={openAlertModal?.buttonText}
          buttonCancelText={openAlertModal?.buttonCancelText}
          confirmar={false}
          handleButton={() => {
            setCheckChanges(false);
            setOpenAlertModal({ open: false });
            if (openAlertModal.callback) {
              openAlertModal.callback(...(openAlertModal?.params || []));
              return;
            }
            setElementSelected(null);
            setElementONUSelected(null);
            setConnectionSelected(null);
          }}
          severity={openAlertModal?.severity}
          singleButton={false}
          textContent={openAlertModal?.text}
        />
      </Box>
    </>
  );
});

export default LeafletMapa;
